/**
 * Reindex + indexes API client. Same-origin /api on the Railway server.
 */

const API_URL = '/api';

export type ReindexMode = 'full' | 'delta';
export type ReindexStatus =
  | 'queued'
  | 'preflight'
  | 'awaiting_confirmation'
  | 'running'
  | 'paused'
  | 'awaiting_index'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type ReindexEntityType =
  | 'person'
  | 'company'
  | 'opportunity'
  | 'note'
  | 'list'
  | 'listEntry';

// AI-summary reuse policy. 'auto' reuses an existing summary within the
// server's AI_SUMMARY_TTL_DAYS then regenerates; 'reuse' always reuses if one
// exists; 'force' always regenerates via Anthropic. Distinct from skipAi
// (which omits the summary entirely).
export type AiSummaryMode = 'auto' | 'reuse' | 'force';

export interface ReindexJob {
  id: string;
  entityType: ReindexEntityType;
  targetIndex: string;
  mode: ReindexMode;
  status: ReindexStatus;
  skipAi: boolean;
  aiSummaryMode: AiSummaryMode;
  totalEstimate: number | null;
  staleCount: number | null;
  /** Operator opted to delete stale IDs from OS at the awaiting_confirmation gate. */
  deleteStaleIds: boolean;
  /** Live counter of stale docs successfully removed from OS this job. */
  staleDeletedCount: number;
  enqueuedCount: number;
  ackedCount: number;
  errorCount: number;
  createdBy: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  lastHeartbeat: string | null;
  errorMessage: string | null;
}

export interface ReindexJobDetail extends ReindexJob {
  events: Array<{
    id: string;
    level: 'info' | 'warn' | 'error';
    message: string;
    meta: Record<string, unknown> | null;
    createdAt: string;
  }>;
  cursors: Array<{
    id: string;
    scope: string;
    cursor: string | null;
    updatedAt: string;
  }>;
  /** During preflight only: number of missing IDs persisted so far.
   *  Null once preflight is done (totalEstimate has the final number). */
  preflightMissingFound: number | null;
}

export interface OpenSearchIndexInfo {
  name: string;
  health: string;
  docsCount: number;
  storeSize: string;
  aliases: string[];
  isAliasTarget: boolean;
}

export interface IndexListResponse {
  entityType: ReindexEntityType | null;
  currentAlias: string | null;
  indices: OpenSearchIndexInfo[];
}

export interface EntityTypeDescriptor {
  key: ReindexEntityType;
  displayName: string;
  pluralName: string;
  aliasName: string;
}

export interface EntityTypesResponse {
  enabled: EntityTypeDescriptor[];
  disabled: Array<{
    key: string;
    displayName: string;
    pluralName: string;
    reason: string;
  }>;
}

async function apiFetch<T>(endpoint: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${endpoint}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const error = await res.text();
    throw new Error(`API Error (${res.status}): ${error}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export function listIndexes(entityType?: ReindexEntityType): Promise<IndexListResponse> {
  return apiFetch<IndexListResponse>(
    `/indexes${entityType ? `?entityType=${entityType}` : ''}`,
  );
}

export function getEntityTypes(): Promise<EntityTypesResponse> {
  return apiFetch<EntityTypesResponse>('/indexes/entity-types');
}

export function listJobs(filters?: {
  entityType?: ReindexEntityType;
  status?: ReindexStatus;
  limit?: number;
}): Promise<{ jobs: ReindexJob[] }> {
  const params = new URLSearchParams();
  if (filters?.entityType) params.set('entityType', filters.entityType);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.limit) params.set('limit', String(filters.limit));
  const q = params.toString();
  return apiFetch<{ jobs: ReindexJob[] }>(`/jobs${q ? `?${q}` : ''}`);
}

export function getJob(jobId: string): Promise<{ job: ReindexJobDetail }> {
  return apiFetch<{ job: ReindexJobDetail }>(`/jobs/${jobId}`);
}

export function createJob(payload: {
  entityType: ReindexEntityType;
  targetIndex: string;
  mode: ReindexMode;
  /** Where to pull entity IDs from for the preflight phase. Defaults to
   *  affinity (paginated API walk); snowflake reads the ANTHOS_DWH mart. */
  idSource?: 'affinity' | 'snowflake';
  skipAi?: boolean;
  /** AI-summary reuse policy. Defaults to 'auto' server-side. */
  aiSummaryMode?: AiSummaryMode;
}): Promise<{ jobId: string; status: ReindexStatus }> {
  return apiFetch<{ jobId: string; status: ReindexStatus }>(`/jobs`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function pauseJob(jobId: string): Promise<{ status: ReindexStatus }> {
  return apiFetch<{ status: ReindexStatus }>(`/jobs/${jobId}/pause`, { method: 'POST' });
}

/**
 * Resume a paused or awaiting_confirmation job.
 *
 * `deleteStaleIds` is only honored when resuming from awaiting_confirmation —
 * that's the operator's opt-in moment to clean up OS docs the source no
 * longer recognizes. Omit/false to add missing docs only.
 */
export function resumeJob(
  jobId: string,
  opts: { deleteStaleIds?: boolean } = {},
): Promise<{ status: ReindexStatus; deleteStaleIds?: boolean }> {
  return apiFetch<{ status: ReindexStatus; deleteStaleIds?: boolean }>(
    `/jobs/${jobId}/resume`,
    {
      method: 'POST',
      body: JSON.stringify(opts),
    },
  );
}

export function cancelJob(jobId: string): Promise<{ status: ReindexStatus }> {
  return apiFetch<{ status: ReindexStatus }>(`/jobs/${jobId}/cancel`, { method: 'POST' });
}
